// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import supabase from '../lib/supabase';
import { getRememberedReferralCode, clearRememberedReferralCode } from '../services/referralLandingService';

const AuthContext = createContext(null);

const ADMIN_ROLES = new Set(['admin', 'administrator', 'superadmin', 'support']);
const isAdminRole = (r) => !!r && ADMIN_ROLES.has(String(r).toLowerCase());

const INTERCEPTABLE = new Set([
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
    '/2fa',
]);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const [profile, setProfile] = useState(null);
    const [role, setRole] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    /* --------------------------- loadProfile --------------------------- */
    const loadProfile = useCallback(async (userId) => {
        if (!userId) {
            setProfile(null);
            setRole(null);
            setProfileLoading(false);
            return null;
        }

        setProfileLoading(true);
        try {
            const { data, error: profileError } = await supabase
                .from('profiles')
                .select('id, email, name, phone, avatar_url, locale, timezone, country, role, is_blocked, referral_code, referred_by, created_at')
                .eq('id', userId)
                .maybeSingle();

            if (profileError) {
                // eslint-disable-next-line no-console
                console.warn('[AuthContext] profile fetch failed:', profileError.message);
                setProfile(null);
                setRole('user');
                return null;
            }

            setProfile(data || null);
            setRole(data?.role || 'user');
            return data || null;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[AuthContext] profile fetch threw:', e);
            setProfile(null);
            setRole('user');
            return null;
        } finally {
            setProfileLoading(false);
        }
    }, []);

    const refreshProfile = useCallback(async () => {
        if (!user?.id) return null;
        return loadProfile(user.id);
    }, [user?.id, loadProfile]);

    const getLandingRoute = useCallback((intendedPath, opts = {}) => {
        const effectiveRole = opts.role ?? role;
        const admin = isAdminRole(effectiveRole);

        if (intendedPath && intendedPath !== '/login' && intendedPath !== '/') {
            return intendedPath;
        }
        return admin ? '/admin' : '/dashboard';
    }, [role]);

    const refreshSession = useCallback(async () => {
        if (refreshing) return null;
        try {
            const { data: { session: refreshedSession }, error: refreshError } =
                await supabase.auth.refreshSession();
            if (refreshError) throw refreshError;

            if (refreshedSession) {
                setSession(refreshedSession);
                setUser(refreshedSession.user);
                return refreshedSession;
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Session refresh error:', err);
        } finally {
            setRefreshing(false);
        }
        return null;
    }, [refreshing]);

    /* ------------------------------ init ------------------------------- */
    const initializeAuth = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const { data: { session: currentSession }, error: sessionError } =
                await supabase.auth.getSession();

            if (sessionError) throw sessionError;

            if (currentSession) {
                setSession(currentSession);
                setUser(currentSession.user);
                await loadProfile(currentSession.user.id);
            } else {
                setSession(null);
                setUser(null);
                setProfile(null);
                setRole(null);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Auth initialization error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loadProfile]);

    /* ------------------------------ signIn ----------------------------- */
    const signIn = async (emailOrObj, passwordArg) => {
        const email = typeof emailOrObj === 'object' ? emailOrObj?.email : emailOrObj;
        const password = typeof emailOrObj === 'object' ? emailOrObj?.password : passwordArg;

        if (!email || !password) {
            const msg = 'Email and password are required';
            setError(msg);
            return { success: false, error: msg };
        }

        try {
            setError(null);
            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: String(email).trim(),
                password: String(password),
            });

            if (signInError) throw signInError;

            if (!data?.session) {
                return { success: false, error: 'No session returned' };
            }

            setSession(data.session);
            setUser(data.user);

            const prof = await loadProfile(data.user.id);
            const normalized = String(prof?.role || 'user').toLowerCase();
            const admin = ADMIN_ROLES.has(normalized);

            // ⭐ Check if 2FA challenge is required
            let needsMfa = false;
            try {
                const { data: level } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
                // eslint-disable-next-line no-console
                console.log('[signIn] AAL:', level);
                needsMfa = level?.currentLevel === 'aal1' && level?.nextLevel === 'aal2';
            } catch (mfaErr) {
                // eslint-disable-next-line no-console
                console.warn('[signIn] AAL check failed:', mfaErr);
            }

            const landing = needsMfa
                ? '/2fa'
                : (admin ? '/admin' : '/dashboard');

            // eslint-disable-next-line no-console
            console.log('[signIn] success', {
                email,
                role: prof?.role,
                isAdmin: admin,
                needsMfa,
                landing,
            });

            return {
                success: true,
                data,
                role: prof?.role || 'user',
                isAdmin: admin,
                needsMfa,
                landing,
            };
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Sign in error:', err);
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    /* ------------------------------ signUp ----------------------------- */
    const signUp = async (email, password, userData) => {
        try {
            setError(null);

            const referralCode = getRememberedReferralCode();

            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        ...userData,
                        referral_code: referralCode || null,
                    },
                    emailRedirectTo: `${window.location.origin}/verify-email`,
                },
            });

            if (signUpError) throw signUpError;

            if (referralCode) clearRememberedReferralCode();

            return { success: true, data: signUpData };
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Sign up error:', err);
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    /* ----------------------------- signOut ----------------------------- */
    const signOut = async () => {
        try {
            setError(null);
            const { error: signOutError } = await supabase.auth.signOut();
            if (signOutError) throw signOutError;

            setSession(null);
            setUser(null);
            setProfile(null);
            setRole(null);
            return { success: true };
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Sign out error:', err);
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    const getCurrentUser = useCallback(async () => {
        try {
            const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
            if (userError) throw userError;
            return currentUser;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Get current user error:', err);
            return null;
        }
    }, []);

    /* --------------------------- auth listener ------------------------- */
    useEffect(() => {
        initializeAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, newSession) => {
                switch (event) {
                    case 'SIGNED_IN':
                        setSession(newSession);
                        setUser(newSession?.user || null);
                        if (newSession?.user?.id) {
                            await loadProfile(newSession.user.id);
                        }
                        break;

                    case 'SIGNED_OUT':
                        setSession(null);
                        setUser(null);
                        setProfile(null);
                        setRole(null);
                        setProfileLoading(false);
                        break;

                    case 'TOKEN_REFRESHED':
                        setSession(newSession);
                        setUser(newSession?.user || null);
                        break;

                    case 'USER_UPDATED':
                        if (newSession) setUser(newSession.user);
                        break;

                    default:
                        break;
                }
            }
        );

        return () => subscription.unsubscribe();
    }, [initializeAuth, loadProfile]);

    const value = {
        user,
        session,
        loading,
        error,
        refreshing,

        profile,
        role,
        profileLoading,
        isAdmin: isAdminRole(role),

        signIn,
        signUp,
        signOut,
        refreshSession,
        getCurrentUser,

        refreshProfile,
        getLandingRoute,
        isAdminRole,

        isAuthenticated: !!(user && session),

        interceptablePaths: INTERCEPTABLE,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};